/**
 * @fileoverview Effective-capabilities API call.
 * @description Reads GET /authorization/me/capabilities — what the signed-in user may do, as
 * permission grants — following its cursor until every page is loaded (the endpoint is
 * cursor-paged and has no `meta`). Each grant is one (permission, role assignment, scope) row;
 * rows are never merged across scopes. A rejected cursor (400 / 0001) restarts the paging once
 * from the first page, as the backend's integration guide asks. Outages (429 / 2021, 503 / 2020)
 * are thrown, never turned into "no permissions" — an empty `grants` list is the only answer that
 * means that.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'

import type { ApiError } from '@/shared/types/http.types'

export type CapabilityScopeType = 'PLATFORM' | 'TENANT' | 'REGION' | 'BRANCH' | 'SELF'

export interface CapabilityGrant {
  permissionCode: string
  roleId: string
  assignmentId: string
  scopeId: string
  scope: {
    scopeType: CapabilityScopeType
    tenantId: string | null
    regionId: string | null
    branchId: string | null
  }
  requiresStepUpMfa: boolean
  validFrom: string | null
  validUntil: string | null
}

export interface CapabilitiesPage {
  userId: string
  evaluatedAt: string
  grants: CapabilityGrant[]
  nextCursor: string | null
}

const PAGE_LIMIT = 100
// A safety stop well above any real grant count, so a misbehaving cursor can't loop forever.
const MAX_PAGES = 50

/**
 * @description Fetches one page of the caller's capability grants.
 * @param {string} [cursor] - The previous page's nextCursor, or omitted for the first page.
 * @returns {Promise<CapabilitiesPage>} The page.
 */
async function getCapabilitiesPage(cursor?: string): Promise<CapabilitiesPage> {
  const { data } = await httpClient.get<CapabilitiesPage>('/authorization/me/capabilities', {
    params: { limit: PAGE_LIMIT, ...(cursor && { cursor }) },
    skipAccessRefresh: true,
  })
  return data
}

/**
 * @description Loads every page from the first one.
 * @returns {Promise<CapabilityGrant[]>} Every grant, in the backend's order.
 */
async function loadAllPages(): Promise<CapabilityGrant[]> {
  const grants: CapabilityGrant[] = []
  let cursor: string | undefined

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const result = await getCapabilitiesPage(cursor)
    grants.push(...result.grants)

    if (!result.nextCursor) {
      return grants
    }

    cursor = result.nextCursor
  }

  return grants
}

/**
 * @description Loads all of the signed-in user's capability grants.
 * @returns {Promise<CapabilityGrant[]>} Every grant across every page.
 */
export async function getCapabilityGrants(): Promise<CapabilityGrant[]> {
  try {
    return await loadAllPages()
  } catch (error) {
    if ((error as ApiError | undefined)?.code === ERROR_CODES.VALIDATION_ERROR) {
      return loadAllPages()
    }

    throw error
  }
}
